import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Bottone, Modale } from './base.tsx'

/**
 * Uscita da un modulo con modifiche non salvate (CHI-08).
 *
 * L'applicazione usa `BrowserRouter`, che non offre `useBlocker`: il blocco della navigazione
 * esiste solo con i router «dati» (`createBrowserRouter`), e il passaggio tocca `main.tsx`, che le
 * corsie non riordinano. Finche' l'integrazione non lo decide, l'uscita si intercetta qui:
 *
 * - collegamenti interni (menu, marchio, «torna alla scheda»): clic intercettato in cattura sul
 *   documento, prima del gestore di React Router;
 * - pulsante Indietro del browser: una voce di cronologia uguale alla corrente, aggiunta quando il
 *   modulo diventa modificato; il ritorno su quella voce non cambia pagina e apre la domanda;
 * - chiusura della scheda del browser o ricaricamento: `beforeunload`, con la domanda del browser.
 *
 * Restano fuori le uscite che non passano da un collegamento, come «Esci» nella testata: conferma
 * su «Esci» o passaggio a `createBrowserRouter` sono una domanda aperta (docs/06-integrazione-onda1.md,
 * § 9.5, domanda 11).
 */

const INDIETRO = Symbol('indietro')
type Destinazione = string | typeof INDIETRO

export function usaUscitaProtetta(attiva: boolean, alloScarto?: () => void) {
  const vaiA = useNavigate()
  const [inSospeso, setInSospeso] = useState<Destinazione | null>(null)
  const attivaRef = useRef(attiva)
  const scartoRef = useRef(alloScarto)
  /** Voce di cronologia aggiunta come sentinella per il pulsante Indietro. */
  const armata = useRef(false)
  /** Il `popstate` provocato da noi togliendo la sentinella non deve aprire la domanda. */
  const ignoraPop = useRef(false)
  /** Dopo l'uscita confermata nessun ascoltatore interviene piu'. */
  const uscito = useRef(false)

  attivaRef.current = attiva
  scartoRef.current = alloScarto
  const protetta = () => attivaRef.current && !uscito.current

  useEffect(() => {
    if (uscito.current) return
    if (attiva && !armata.current) {
      window.history.pushState(window.history.state, '', window.location.href)
      armata.current = true
    } else if (!attiva && armata.current) {
      // Il modulo e' tornato come all'apertura: la sentinella si toglie, altrimenti servirebbero
      // due pressioni di Indietro per lasciare la pagina.
      armata.current = false
      ignoraPop.current = true
      window.history.back()
    }
  }, [attiva])

  useEffect(() => {
    const primaDiUscire = (e: BeforeUnloadEvent) => {
      if (!protetta()) return
      e.preventDefault()
      e.returnValue = ''
    }
    // La pagina se ne va davvero (l'utente ha confermato la domanda del browser): lo scarto si
    // registra con un invio che sopravvive alla chiusura.
    const allaChiusura = () => { if (protetta()) scartoRef.current?.() }
    const clic = (e: MouseEvent) => {
      if (!protetta() || e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin) return
      const verso = url.pathname + url.search + url.hash
      if (verso === window.location.pathname + window.location.search + window.location.hash) return
      e.preventDefault()
      e.stopPropagation()
      setInSospeso(verso)
    }
    const indietro = () => {
      if (ignoraPop.current) { ignoraPop.current = false; return }
      if (!protetta() || !armata.current) return
      // Il browser e' gia' tornato sulla voce originale, con lo stesso indirizzo: React Router
      // ridisegna la stessa pagina senza perdere lo stato. Si rimette la sentinella e si chiede.
      window.history.pushState(window.history.state, '', window.location.href)
      setInSospeso(INDIETRO)
    }
    window.addEventListener('beforeunload', primaDiUscire)
    window.addEventListener('pagehide', allaChiusura)
    window.addEventListener('popstate', indietro)
    document.addEventListener('click', clic, true)
    return () => {
      window.removeEventListener('beforeunload', primaDiUscire)
      window.removeEventListener('pagehide', allaChiusura)
      window.removeEventListener('popstate', indietro)
      document.removeEventListener('click', clic, true)
    }
    // Gli ascoltatori leggono lo stato dai riferimenti: si registrano una volta sola.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Esce senza altre domande: verso `verso` se indicato, altrimenti verso l'uscita in sospeso.
   * La sentinella, se c'e', viene sostituita o saltata: la cronologia resta quella di prima.
   */
  const esci = useCallback((verso?: string) => {
    const destinazione: Destinazione = verso ?? inSospeso ?? '/'
    uscito.current = true
    setInSospeso(null)
    const conSentinella = armata.current
    armata.current = false
    if (destinazione === INDIETRO) window.history.go(conSentinella ? -2 : -1)
    else vaiA(destinazione, { replace: conSentinella })
  }, [inSospeso, vaiA])

  return {
    /** Vero mentre la domanda «Salva / Non salvare» e' aperta. */
    domanda: inSospeso !== null,
    rimani: useCallback(() => setInSospeso(null), []),
    esci,
  }
}

/**
 * Domanda all'uscita con le due scelte delle Note aggiuntive: «Salva» e «Non salvare». Chiudere la
 * finestra (Esc o «Chiudi») riporta al modulo senza uscire.
 */
export function DomandaUscita({ spiegazione, inCorso, salva, nonSalvare, rimani }: {
  spiegazione: string
  inCorso: boolean
  salva: () => void
  nonSalvare: () => void
  rimani: () => void
}) {
  // La finestra si apre con il fuoco sul primo comando: da tastiera Invio salva, Esc torna al
  // modulo.
  return (
    <Modale titolo="Modifiche non salvate" chiudi={rimani} fuocoIniziale="azioni"
            azioni={
              <>
                <Bottone variante="primario" inCorso={inCorso} onClick={salva}>Salva</Bottone>
                <Bottone variante="critico" disabled={inCorso} onClick={nonSalvare}>Non salvare</Bottone>
              </>
            }>
      <p>{spiegazione}</p>
    </Modale>
  )
}
